/**
 * The question to authors who were here before `public_tips` (the owner, 2 November 2026): show the
 * amount of a tip beside the reader's message? Until they answer the amounts stay hidden. New authors
 * have it from their first acceptance of the documents (`TermsService`), so they are never asked.
 * Asked once: either answer is recorded, and Settings changes it later.
 */
import { useCallback, useState } from 'react';
import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { useCabinetApi } from '../../features/cabinet/api';
import { ErrorState } from '../../features/cabinet/components';
import { useAction, useAsync } from '../../features/cabinet/state';
import { useTranslation } from '../../i18n';

export function TipsConsentNotice() {
  const { t } = useTranslation('dashboard');
  const api = useCabinetApi();
  const read = useCallback((signal: AbortSignal) => api.getOwnProfile(signal), [api]);
  const profile = useAsync(read, 'overview:tips-consent');
  const [answered, setAnswered] = useState(false);
  const save = useAction((enabled: boolean) => api.setTipsVisibility({ enabled }));

  const asked = profile.data?.tipsVisibility;
  // A failed or absent read asks nothing: this is not worth an error on the Overview.
  if (answered || asked === undefined || (asked.changedAt ?? null) !== null) {
    return null;
  }

  const answer = (enabled: boolean): void => {
    void save.run(enabled).then((recorded) => {
      if (recorded !== undefined) {
        setAnswered(true);
      }
    });
  };

  return (
    <Alert variant="light" title={t('overview.tipsConsent.title')}>
      <Stack gap="sm">
        <Text size="sm">{t('overview.tipsConsent.body')}</Text>
        <Group gap="sm">
          <Button
            size="xs"
            radius="xl"
            loading={save.isRunning}
            onClick={() => {
              answer(true);
            }}
          >
            {t('overview.tipsConsent.show')}
          </Button>
          <Button
            size="xs"
            radius="xl"
            variant="default"
            disabled={save.isRunning}
            onClick={() => {
              answer(false);
            }}
          >
            {t('overview.tipsConsent.hide')}
          </Button>
        </Group>
        {save.error === undefined ? null : (
          <ErrorState title={t('overview.tipsConsent.failed')} error={save.error} />
        )}
      </Stack>
    </Alert>
  );
}
